import { useEffect, useMemo, useRef, useState } from 'react';
import styles from './DatePicker.module.scss';

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

// Date → 'YYYY-MM-DD' (로컬 시간 기준)
function toDateString(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// 'YYYY-MM-DD' → '2026년 10월 15일 (목)'
function formatLabel(value: string): string {
  const [y, m, d] = value.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  return `${y}년 ${m}월 ${d}일 (${WEEKDAYS[date.getDay()]})`;
}

interface DatePickerProps {
  /** 'YYYY-MM-DD' 또는 빈 문자열 */
  value: string;
  onChange: (value: string) => void;
  /** 선택 가능한 최소 날짜 'YYYY-MM-DD' */
  min?: string;
  /** min 보다 이전 날짜를 눌렀을 때 (선택은 되지 않음) */
  onPastSelect?: () => void;
  placeholder?: string;
  /** 입력칸 모양 (페이지의 입력칸 클래스를 그대로 사용) */
  inputClassName?: string;
  /** 오류 표시(빨간 테두리) */
  hasError?: boolean;
  id?: string;
}

/**
 * 입력칸 어디를 눌러도 열리는 달력 (브라우저 기본 달력 대신 사이트 디자인에 맞춘 달력)
 * - 지난 날짜(min 이전)는 흐리게 표시하고, 누르면 onPastSelect 호출
 * - 바깥 클릭 / Esc 로 닫힘
 */
function DatePicker({
  value,
  onChange,
  min,
  onPastSelect,
  placeholder = '날짜 선택',
  inputClassName,
  hasError = false,
  id,
}: DatePickerProps) {
  const today = useMemo(() => toDateString(new Date()), []);
  const [open, setOpen] = useState(false);
  const [view, setView] = useState(() => {
    const base = value ? new Date(`${value}T00:00:00`) : new Date();
    return { year: base.getFullYear(), month: base.getMonth() };
  });
  const rootRef = useRef<HTMLDivElement>(null);

  // 열 때 선택된 날짜(없으면 이번 달)로 이동
  const openPicker = () => {
    const base = value ? new Date(`${value}T00:00:00`) : new Date();
    setView({ year: base.getFullYear(), month: base.getMonth() });
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // 달력 칸: 앞쪽 빈칸 + 1일~말일
  const cells = useMemo(() => {
    const first = new Date(view.year, view.month, 1).getDay();
    const days = new Date(view.year, view.month + 1, 0).getDate();
    const list: (string | null)[] = Array(first).fill(null);
    for (let d = 1; d <= days; d++) list.push(`${view.year}-${pad(view.month + 1)}-${pad(d)}`);
    return list;
  }, [view]);

  const moveMonth = (diff: number) => {
    setView((v) => {
      const d = new Date(v.year, v.month + diff, 1);
      return { year: d.getFullYear(), month: d.getMonth() };
    });
  };

  // 최소 날짜가 있는 달보다 이전 달로는 이동하지 않음
  const minMonthKey = min ? Number(min.slice(0, 4)) * 12 + Number(min.slice(5, 7)) - 1 : -Infinity;
  const canGoPrev = view.year * 12 + view.month > minMonthKey;

  const select = (day: string) => {
    if (min && day < min) {
      onPastSelect?.();
      return;
    }
    onChange(day);
    setOpen(false);
  };

  return (
    <div className={styles.root} ref={rootRef}>
      <button
        type="button"
        id={id}
        className={`${inputClassName ?? ''} ${styles.trigger} ${open ? styles.triggerOpen : ''} ${hasError ? styles.triggerError : ''}`}
        onClick={() => (open ? setOpen(false) : openPicker())}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <span className={value ? styles.value : styles.placeholder}>{value ? formatLabel(value) : placeholder}</span>
        <svg className={styles.calIcon} viewBox="0 0 24 24" aria-hidden="true">
          <rect x="3" y="5" width="18" height="16" rx="2" />
          <path d="M16 3v4M8 3v4M3 10h18" />
        </svg>
      </button>

      {open && (
        <div className={styles.panel} role="dialog" aria-label="날짜 선택">
          <div className={styles.head}>
            <button
              type="button"
              className={styles.nav}
              onClick={() => moveMonth(-1)}
              disabled={!canGoPrev}
              aria-label="이전 달"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="m15 18-6-6 6-6" />
              </svg>
            </button>
            <strong className={styles.month}>
              {view.year}년 {view.month + 1}월
            </strong>
            <button type="button" className={styles.nav} onClick={() => moveMonth(1)} aria-label="다음 달">
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="m9 18 6-6-6-6" />
              </svg>
            </button>
          </div>

          <div className={styles.weekdays}>
            {WEEKDAYS.map((w, i) => (
              <span key={w} className={i === 0 ? styles.sun : i === 6 ? styles.sat : ''}>
                {w}
              </span>
            ))}
          </div>

          <div className={styles.grid}>
            {cells.map((day, i) => {
              if (!day) return <span key={`empty-${i}`} />;
              const weekday = i % 7;
              const isPast = !!min && day < min;
              const cls = [
                styles.day,
                weekday === 0 ? styles.sun : weekday === 6 ? styles.sat : '',
                day === today ? styles.today : '',
                day === value ? styles.selected : '',
                isPast ? styles.past : '',
              ].join(' ');
              return (
                <button key={day} type="button" className={cls} onClick={() => select(day)} aria-pressed={day === value}>
                  {Number(day.slice(8))}
                </button>
              );
            })}
          </div>

          <div className={styles.foot}>
            <button
              type="button"
              className={styles.footBtn}
              onClick={() => {
                onChange('');
                setOpen(false);
              }}
            >
              지우기
            </button>
            <button type="button" className={`${styles.footBtn} ${styles.footPrimary}`} onClick={() => select(today)}>
              오늘
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default DatePicker;
